import React from "react";
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Grid,
  Divider
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(() => ({
  printContainer: {
    padding: "8mm 5mm", // Reduzido de 20mm para 8mm top/bottom
    backgroundColor: "#fff",
    color: "#000",
    fontFamily: "'Roboto', 'Arial', sans-serif",
    fontSize: "8pt", // Reduzido de 10pt para 8pt
    lineHeight: "1.3", // Reduzido de 1.5 para 1.3
    "& *": {
      boxSizing: "border-box",
    },
  },
  header: {
    textAlign: "center",
    marginBottom: "5mm", // Reduzido de 15mm para 5mm
    paddingBottom: "2mm", // Reduzido de 5mm para 2mm
    borderBottom: "2px solid #000", // Reduzido de 3px para 2px
  },
  title: {
    fontSize: "14pt", // Reduzido de 18pt para 14pt
    fontWeight: "bold",
    marginBottom: "1mm", // Reduzido de 3mm para 1mm
    textTransform: "uppercase",
    letterSpacing: "0.5px", // Reduzido de 1px para 0.5px
  },
  subtitle: {
    fontSize: "7pt", // Reduzido de 9pt para 7pt
    color: "#666",
    marginTop: "1mm", // Reduzido de 2mm para 1mm
  },
  clientName: {
    fontSize: "11pt", // Reduzido de 14pt para 11pt
    fontWeight: "bold",
    marginTop: "1mm", // Reduzido de 3mm para 1mm
    color: "#000",
  },
  section: {
    marginTop: "3mm", // Reduzido de 8mm para 3mm
    marginBottom: "3mm", // Reduzido de 8mm para 3mm
    pageBreakInside: "avoid",
  },
  sectionTitle: {
    fontSize: "10pt", // Reduzido de 12pt para 10pt
    fontWeight: "bold",
    marginBottom: "2mm", // Reduzido de 3mm para 2mm
    paddingBottom: "1mm", // Reduzido de 2mm para 1mm
    borderBottom: "1px solid #333", // Reduzido de 2px para 1px
    textTransform: "uppercase",
    letterSpacing: "0.3px", // Reduzido de 0.5px para 0.3px
  },
  infoGrid: {
    marginTop: "2mm", // Reduzido de 3mm para 2mm
  },
  infoLabel: {
    fontSize: "7pt", // Reduzido de 9pt para 7pt
    fontWeight: "bold",
    color: "#333",
    marginBottom: "0.5mm", // Reduzido de 1mm para 0.5mm
  },
  infoValue: {
    fontSize: "8pt", // Reduzido de 10pt para 8pt
    color: "#000",
    marginBottom: "2mm", // Reduzido de 3mm para 2mm
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "2mm", // Reduzido de 3mm para 2mm
    pageBreakInside: "avoid",
    "& th": {
      backgroundColor: "#333",
      color: "#fff",
      padding: "1.5mm", // Reduzido de 3mm para 1.5mm
      fontSize: "7pt", // Reduzido de 9pt para 7pt
      fontWeight: "bold",
      textAlign: "left",
      border: "1px solid #000",
    },
    "& td": {
      padding: "1mm 1.5mm", // Reduzido de 2mm 3mm para 1mm 1.5mm
      fontSize: "7pt", // Reduzido de 9pt para 7pt
      border: "1px solid #666",
      color: "#000",
    },
  },
  contactBox: {
    border: "1px solid #666",
    padding: "2mm", // Reduzido de 3mm para 2mm
    marginBottom: "2mm", // Reduzido de 3mm para 2mm
    borderRadius: "1mm", // Reduzido de 2mm para 1mm
    pageBreakInside: "avoid",
    backgroundColor: "#f9f9f9",
  },
  contactName: {
    fontSize: "8pt", // Reduzido de 10pt para 8pt
    fontWeight: "bold",
    marginBottom: "1mm", // Reduzido de 2mm para 1mm
  },
  contactInfo: {
    fontSize: "7pt", // Reduzido de 9pt para 7pt
    marginBottom: "0.5mm", // Reduzido de 1mm para 0.5mm
    color: "#333",
  },
  observationBox: {
    border: "1px solid #999",
    padding: "2mm", // Reduzido de 3mm para 2mm
    marginTop: "2mm", // Reduzido de 3mm para 2mm
    backgroundColor: "#f5f5f5",
    borderRadius: "1mm", // Reduzido de 2mm para 1mm
    pageBreakInside: "avoid",
  },
  observationTitle: {
    fontSize: "7pt", // Reduzido de 9pt para 7pt
    fontWeight: "bold",
    marginBottom: "1mm", // Reduzido de 2mm para 1mm
    color: "#333",
  },
  observationText: {
    fontSize: "7pt", // Reduzido de 9pt para 7pt
    whiteSpace: "pre-wrap",
    color: "#000",
  },
  divider: {
    margin: "2mm 0", // Reduzido de 5mm para 2mm
    border: "none",
    borderTop: "1px dashed #999",
  },
  emptyMessage: {
    fontSize: "7pt", // Reduzido de 9pt para 7pt
    color: "#999",
    fontStyle: "italic",
    textAlign: "center",
    padding: "3mm", // Reduzido de 5mm para 3mm
  },
  footer: {
    marginTop: "3mm", // Reduzido de 10mm para 3mm
    paddingTop: "2mm", // Reduzido de 5mm para 2mm
    borderTop: "1px solid #000", // Reduzido de 2px para 1px
    textAlign: "center",
    fontSize: "6pt", // Reduzido de 8pt para 6pt
    color: "#666",
  },
}));

const ClienteResumoPrintTemplate = React.forwardRef(({ cliente, pedidos, historicoRelacionamento }, ref) => {
  const classes = useStyles();

  const formatCNPJ = (cnpj) => {
    if (!cnpj) return "-";
    return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  };

  const formatCPF = (cpf) => {
    if (!cpf) return "-";
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  };

  const formatPhone = (ddd, phone) => {
    if (!phone) return "-";
    return ddd ? `(${ddd}) ${phone}` : phone;
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value) => {
    if (!value || value === 0) return "R$ 0,00";
    return new Intl.NumberFormat('pt-BR', { 
      style: 'currency', 
      currency: 'BRL' 
    }).format(value);
  };

  return (
    <div ref={ref} className={classes.printContainer}>
      {/* Cabeçalho */}
      <Box className={classes.header}>
        <Typography className={classes.title}>
          Perfil Completo do Cliente
        </Typography>
        <Typography className={classes.subtitle}>
          Data de Emissão: {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR')}
        </Typography>
        <Typography className={classes.clientName}>
          {cliente.NOMECLI || cliente.NOMEFANTASIA || "-"}
        </Typography>
      </Box>

      {/* Informações Cadastrais */}
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>
          Informações Cadastrais
        </Typography>
        <Grid container spacing={1} className={classes.infoGrid}>
          <Grid item xs={6}>
            <Typography className={classes.infoLabel}>Razão Social:</Typography>
            <Typography className={classes.infoValue}>{cliente.NOMECLI || "-"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.infoLabel}>Nome Fantasia:</Typography>
            <Typography className={classes.infoValue}>{cliente.NOMEFANTASIA || "-"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.infoLabel}>Tipo:</Typography>
            <Typography className={classes.infoValue}>
              {cliente.PESSOA === 'J' ? 'Pessoa Jurídica' : cliente.PESSOA === 'F' ? 'Pessoa Física' : "-"}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.infoLabel}>Status:</Typography>
            <Typography className={classes.infoValue}>
              {cliente.STATUS === 'A' ? 'Ativo' : cliente.STATUS === 'I' ? 'Inativo' : "-"}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.infoLabel}>CNPJ:</Typography>
            <Typography className={classes.infoValue}>{formatCNPJ(cliente.CGCCLI)}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.infoLabel}>CPF:</Typography>
            <Typography className={classes.infoValue}>{formatCPF(cliente.CPFCLI)}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.infoLabel}>Inscrição Estadual:</Typography>
            <Typography className={classes.infoValue}>{cliente.INSCESTCLI || "-"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.infoLabel}>Data de Cadastro:</Typography>
            <Typography className={classes.infoValue}>{formatDate(cliente.DTCADASTRO)}</Typography>
          </Grid>
        </Grid>
      </Box>

      <Divider className={classes.divider} />

      {/* Endereço e Contato */}
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>
          Endereço e Contato
        </Typography>
        <Grid container spacing={1} className={classes.infoGrid}>
          <Grid item xs={12}>
            <Typography className={classes.infoLabel}>Endereço:</Typography>
            <Typography className={classes.infoValue}>
              {cliente.ENDCLI || "-"}
              {cliente.BAIRROCLI && `, ${cliente.BAIRROCLI}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.infoLabel}>Cidade/UF:</Typography>
            <Typography className={classes.infoValue}>
              {cliente.CIDADECLI || "-"} - {cliente.ESTADOCLI || "-"}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.infoLabel}>CEP:</Typography>
            <Typography className={classes.infoValue}>{cliente.CEPCLI || "-"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.infoLabel}>Telefone:</Typography>
            <Typography className={classes.infoValue}>{formatPhone(cliente.DDDCLI, cliente.FONECLI)}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.infoLabel}>Celular:</Typography>
            <Typography className={classes.infoValue}>{formatPhone(cliente.DDDCELULAR, cliente.CELULAR)}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography className={classes.infoLabel}>E-mail:</Typography>
            <Typography className={classes.infoValue}>{cliente.E_MAIL || "-"}</Typography>
          </Grid>
        </Grid>
      </Box>

      <Divider className={classes.divider} />

      {/* Histórico de Relacionamento */}
      {historicoRelacionamento && historicoRelacionamento.length > 0 && (
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Histórico de Relacionamento
          </Typography>
          <TableContainer>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th style={{ width: "12%" }}>Data</th>
                  <th style={{ width: "15%" }}>Contato</th>
                  <th style={{ width: "10%" }}>Meio</th>
                  <th style={{ width: "15%" }}>Resultado</th>
                  <th style={{ width: "33%" }}>Observações</th>
                  <th style={{ width: "15%" }}>Usuário</th>
                </tr>
              </thead>
              <tbody>
                {historicoRelacionamento.map((hist) => (
                  <tr key={hist.id}>
                    <td>{new Date(hist.data_contato).toLocaleDateString('pt-BR')}</td>
                    <td>{hist.contato?.nome || '-'}</td>
                    <td>{hist.meio_contato || '-'}</td>
                    <td>{hist.resultado_visita || '-'}</td>
                    <td>
                      {hist.observacoes && (
                        <div style={{ marginBottom: '1mm' }}>
                          {hist.observacoes.length > 150 
                            ? `${hist.observacoes.substring(0, 150)}...` 
                            : hist.observacoes}
                        </div>
                      )}
                      {hist.potencial_vendas && (
                        <div style={{ fontWeight: 'bold', color: '#000', marginTop: '1mm' }}>
                          Potencial: R$ {parseFloat(hist.potencial_vendas).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </div>
                      )}
                      {hist.motivos && hist.motivos.length > 0 && (
                        <div style={{ marginTop: '1mm', fontSize: '6pt' }}>
                          Motivos: {hist.motivos.map(m => m.motivo).join(', ')}
                        </div>
                      )}
                    </td>
                    <td>{hist.user?.name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableContainer>
        </Box>
      )}

      {historicoRelacionamento && historicoRelacionamento.length > 0 && (
        <Divider className={classes.divider} />
      )}

      {/* Contatos */}
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>
          Contatos Vinculados
        </Typography>
        {cliente.contatos && cliente.contatos.length > 0 ? (
          cliente.contatos.map((contato, index) => (
            <Box key={contato.id || index} className={classes.contactBox}>
              <Typography className={classes.contactName}>
                {contato.nome}
                {contato.cargo && ` - ${contato.cargo}`}
                {contato.principal && " (PRINCIPAL)"}
              </Typography>
              {(contato.telefone || contato.celular) && (
                <Typography className={classes.contactInfo}>
                  Tel: {formatPhone(contato.ddd_telefone, contato.telefone)}
                  {contato.celular && ` / Cel: ${formatPhone(contato.ddd_celular, contato.celular)}`}
                  {contato.whatsapp && " (WhatsApp)"}
                </Typography>
              )}
              {contato.email && (
                <Typography className={classes.contactInfo}>
                  E-mail: {contato.email}
                  {contato.email_secundario && ` / ${contato.email_secundario}`}
                </Typography>
              )}
              {contato.linkedin && (
                <Typography className={classes.contactInfo}>
                  LinkedIn: {contato.linkedin}
                </Typography>
              )}
              {contato.observacoes && (
                <Typography className={classes.contactInfo} style={{ marginTop: '2mm', fontStyle: 'italic' }}>
                  Obs: {contato.observacoes}
                </Typography>
              )}
            </Box>
          ))
        ) : (
          <Typography className={classes.emptyMessage}>
            Nenhum contato cadastrado
          </Typography>
        )}
      </Box>

      <Divider className={classes.divider} />

      {/* Informações Comerciais */}
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>
          Informações Comerciais
        </Typography>
        <TableContainer>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Status</th>
                <th>Tipo Cliente</th>
                <th>Segmento</th>
                <th>Situação CRM</th>
                <th>Limite de Crédito</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{cliente.STATUS === 'A' ? 'ATIVO' : cliente.STATUS === 'I' ? 'INATIVO' : '-'}</td>
                <td>{cliente.TIPO_CLIENTE || cliente.CRM_APLICAVEL || '-'}</td>
                <td>{cliente.segmento || '-'}</td>
                <td>{cliente.CRM_SITUACAO_CLIENTE || '-'}</td>
                <td>{formatCurrency(cliente.LIMITE || cliente.LIMCREDITO)}</td>
              </tr>
            </tbody>
          </table>
        </TableContainer>

        {(cliente.DATA_PRIMEIRA_COMPRA || cliente.DATA_ULTIMA_COMPRA) && (
          <Grid container spacing={1} style={{ marginTop: '2mm' }}>
            {cliente.DATA_PRIMEIRA_COMPRA && (
              <Grid item xs={6}>
                <Typography className={classes.infoLabel}>Primeira Compra:</Typography>
                <Typography className={classes.infoValue}>{formatDate(cliente.DATA_PRIMEIRA_COMPRA)}</Typography>
              </Grid>
            )}
            {cliente.DATA_ULTIMA_COMPRA && (
              <Grid item xs={6}>
                <Typography className={classes.infoLabel}>Última Compra:</Typography>
                <Typography className={classes.infoValue}>{formatDate(cliente.DATA_ULTIMA_COMPRA)}</Typography>
              </Grid>
            )}
          </Grid>
        )}
      </Box>

      {/* Metas e Ações de Vendas */}
      {(cliente.metas_vendas || cliente.potencial_vendas || cliente.acoes_vendas) && (
        <>
          <Divider className={classes.divider} />
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              Metas, Potencial e Ações de Vendas
            </Typography>
            {cliente.metas_vendas && (
              <Box className={classes.observationBox}>
                <Typography className={classes.observationTitle}>Metas de Vendas:</Typography>
                <Typography className={classes.observationText}>{cliente.metas_vendas}</Typography>
              </Box>
            )}
            {cliente.potencial_vendas && (
              <Box className={classes.observationBox}>
                <Typography className={classes.observationTitle}>Potencial de Vendas:</Typography>
                <Typography className={classes.observationText}>{cliente.potencial_vendas}</Typography>
              </Box>
            )}
            {cliente.acoes_vendas && (
              <Box className={classes.observationBox}>
                <Typography className={classes.observationTitle}>Ações de Vendas:</Typography>
                <Typography className={classes.observationText}>{cliente.acoes_vendas}</Typography>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Observações */}
      {(cliente.OBS || cliente.OBSCLI || cliente.ANOTACAO) && (
        <>
          <Divider className={classes.divider} />
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              Observações e Anotações
            </Typography>
            {cliente.OBS && (
              <Box className={classes.observationBox}>
                <Typography className={classes.observationTitle}>Observações:</Typography>
                <Typography className={classes.observationText}>{cliente.OBS}</Typography>
              </Box>
            )}
            {cliente.OBSCLI && (
              <Box className={classes.observationBox}>
                <Typography className={classes.observationTitle}>Observações do Cliente:</Typography>
                <Typography className={classes.observationText}>{cliente.OBSCLI}</Typography>
              </Box>
            )}
            {cliente.ANOTACAO && (
              <Box className={classes.observationBox}>
                <Typography className={classes.observationTitle}>Anotações:</Typography>
                <Typography className={classes.observationText}>{cliente.ANOTACAO}</Typography>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Seção de Pedidos */}
      {pedidos && (
        <>
          <Divider className={classes.divider} />
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              Histórico de Pedidos
            </Typography>
            
            {/* Grid com resumo dos pedidos */}
            <Grid container spacing={1} className={classes.infoGrid}>
              <Grid item xs={3}>
                <Typography className={classes.infoLabel}>Total de Pedidos:</Typography>
                <Typography className={classes.infoValue} style={{ fontWeight: 'bold' }}>
                  {pedidos.total || 0}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography className={classes.infoLabel}>Valor Total:</Typography>
                <Typography className={classes.infoValue} style={{ fontWeight: 'bold' }}>
                  {formatCurrency(pedidos.valor_total)}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography className={classes.infoLabel}>Primeira Compra:</Typography>
                <Typography className={classes.infoValue}>
                  {pedidos.primeira_compra ? formatDate(pedidos.primeira_compra.data_pedido) : '-'}
                </Typography>
                {pedidos.primeira_compra && (
                  <Typography className={classes.infoValue} style={{ fontSize: '6pt', color: '#666' }}>
                    {pedidos.primeira_compra.numero_pedido}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={3}>
                <Typography className={classes.infoLabel}>Última Compra:</Typography>
                <Typography className={classes.infoValue}>
                  {pedidos.ultima_compra ? formatDate(pedidos.ultima_compra.data_pedido) : '-'}
                </Typography>
                {pedidos.ultima_compra && (
                  <Typography className={classes.infoValue} style={{ fontSize: '6pt', color: '#666' }}>
                    {pedidos.ultima_compra.numero_pedido}
                  </Typography>
                )}
              </Grid>
            </Grid>

            {/* Tabela de Pedidos Recentes */}
            {pedidos.lista && pedidos.lista.length > 0 && (
              <>
                <Typography className={classes.sectionTitle} style={{ marginTop: '3mm', fontSize: '9pt' }}>
                  Últimos Pedidos
                </Typography>
                <TableContainer>
                  <table className={classes.table}>
                    <thead>
                      <tr>
                        <th style={{ width: '15%' }}>Pedido</th>
                        <th style={{ width: '12%' }}>Data</th>
                        <th style={{ width: '15%' }}>Status</th>
                        <th style={{ width: '15%', textAlign: 'right' }}>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pedidos.lista.slice(0, 10).map((pedido) => (
                        <tr key={pedido.id}>
                          <td>{pedido.numero_pedido || '-'}</td>
                          <td>{formatDate(pedido.data_pedido)}</td>
                          <td>
                            {pedido.status === 'pendente' && 'PENDENTE'}
                            {pedido.status === 'confirmado' && 'CONFIRMADO'}
                            {pedido.status === 'em_producao' && 'EM PRODUÇÃO'}
                            {pedido.status === 'enviado' && 'ENVIADO'}
                            {pedido.status === 'entregue' && 'ENTREGUE'}
                            {pedido.status === 'cancelado' && 'CANCELADO'}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(pedido.valor_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableContainer>
              </>
            )}
          </Box>
        </>
      )}

      {/* Produtos Mais Comprados */}
      {pedidos?.produtos_mais_comprados && pedidos.produtos_mais_comprados.length > 0 && (
        <>
          <Divider className={classes.divider} />
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              Relação dos Produtos que Mais Compra (Limitado aos 25 Primeiros Colocados)
            </Typography>
            <TableContainer>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th style={{ width: '40%' }}>Produto</th>
                    <th style={{ width: '12%', textAlign: 'right' }}>Menor Valor</th>
                    <th style={{ width: '12%', textAlign: 'right' }}>Maior Valor</th>
                    <th style={{ width: '12%', textAlign: 'right' }}>Valor Médio</th>
                    <th style={{ width: '12%', textAlign: 'center' }}>Qtd.</th>
                    <th style={{ width: '12%', textAlign: 'right' }}>Valor Total</th>
                  </tr>
                </thead>
                <tbody>
                  {pedidos.produtos_mais_comprados.map((produto, index) => (
                    <tr key={index}>
                      <td>
                        {produto.descricao}
                        {produto.codigo_produto && (
                          <div style={{ fontSize: '6pt', color: '#666' }}>
                            Cód: {produto.codigo_produto}
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(produto.menor_valor)}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(produto.maior_valor)}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(produto.valor_total / produto.quantidade_total)}</td>
                      <td style={{ textAlign: 'center' }}>
                        {new Intl.NumberFormat('pt-BR').format(produto.quantidade_total)}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(produto.valor_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableContainer>
          </Box>
        </>
      )}

      <Divider className={classes.divider} />

      {/* Informações Complementares */}
      {(cliente.contribuinte_icms || cliente.insc_municipal || cliente.suframa || cliente.site || cliente.data_fundacao) && (
        <>
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              Informações Complementares
            </Typography>
            <Grid container spacing={1} className={classes.infoGrid}>
              {cliente.contribuinte_icms && (
                <Grid item xs={6}>
                  <Typography className={classes.infoLabel}>Contribuinte ICMS:</Typography>
                  <Typography className={classes.infoValue}>{cliente.contribuinte_icms}</Typography>
                </Grid>
              )}
              {cliente.insc_municipal && (
                <Grid item xs={6}>
                  <Typography className={classes.infoLabel}>Inscrição Municipal:</Typography>
                  <Typography className={classes.infoValue}>{cliente.insc_municipal}</Typography>
                </Grid>
              )}
              {cliente.suframa && (
                <Grid item xs={6}>
                  <Typography className={classes.infoLabel}>SUFRAMA:</Typography>
                  <Typography className={classes.infoValue}>{cliente.suframa}</Typography>
                </Grid>
              )}
              {cliente.site && (
                <Grid item xs={6}>
                  <Typography className={classes.infoLabel}>Website:</Typography>
                  <Typography className={classes.infoValue}>{cliente.site}</Typography>
                </Grid>
              )}
              {cliente.data_fundacao && (
                <Grid item xs={6}>
                  <Typography className={classes.infoLabel}>Data de Fundação:</Typography>
                  <Typography className={classes.infoValue}>{formatDate(cliente.data_fundacao)}</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </>
      )}

      {/* Rodapé */}
      <Box className={classes.footer}>
        <Typography>
          {process.env.REACT_APP_PRINT_FOOTER || 'Documento gerado automaticamente pelo sistema'}
        </Typography>
        <Typography>
          Este relatório é confidencial e destinado exclusivamente para uso interno
        </Typography>
      </Box>
    </div>
  );
});

ClienteResumoPrintTemplate.displayName = 'ClienteResumoPrintTemplate';

export default ClienteResumoPrintTemplate;
